import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPassword({ kind = "contractor" }) {
  const isAgent = kind === "agent";
  const idParam = isAgent ? "aid" : "cid";
  const validatePath = isAgent ? "agent/invite" : "contractor/invite";
  const acceptPath = isAgent ? "agent/accept-invite" : "contractor/accept-invite";
  const loginPath = isAgent ? "/agent/login" : "/portal/login";
  const portalPath = isAgent ? "/agent" : "/portal";
  const loginLabel = isAgent ? "Go to Agent Login" : "Go to Contractor Login";

  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const entityId = params.get(idParam) || "";
  const { applySession } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState("checking"); // checking | valid | invalid
  const [info, setInfo] = useState(null);
  const [invalidMsg, setInvalidMsg] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token || !entityId) { setState("invalid"); setInvalidMsg("This invite link is missing information."); return; }
      try {
        const { data } = await axios.get(`${API}/${validatePath}`, { params: { token, [idParam]: entityId } });
        if (active) { setInfo(data); setState("valid"); }
      } catch (err) {
        const d = err?.response?.data?.detail;
        if (active) { setInvalidMsg(typeof d === "string" ? d : "This invite link is invalid."); setState("invalid"); }
      }
    })();
    return () => { active = false; };
  }, [token, entityId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/${acceptPath}`, { token, [idParam]: entityId, password });
      applySession(data);
      navigate(portalPath);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not set your password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#EAF7FC] px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="rounded-[1.5rem] bg-white p-8 md:p-10 shadow-[0_16px_48px_rgba(19,57,85,0.12)] border border-[rgba(19,57,85,0.06)]" data-testid="set-password-card">
          {state === "checking" && (
            <div className="grid place-items-center py-8"><Loader2 className="animate-spin text-[#133955]" size={28} /></div>
          )}
          {state === "invalid" && (
            <div className="text-center py-4" data-testid="invite-invalid">
              <h1 className="font-display text-xl font-bold text-[#133955] mb-2">Invite unavailable</h1>
              <p className="text-sm text-[#173042]/70">{invalidMsg}</p>
              <a href={loginPath} className="inline-block mt-6 text-[#133955] font-semibold hover:underline">{loginLabel} →</a>
            </div>
          )}
          {state === "valid" && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#133955] text-white"><KeyRound size={20} /></span>
                <div>
                  <h1 className="font-display text-xl font-bold text-[#133955]">Set your password</h1>
                  <p className="text-sm text-[#173042]/60">{info?.dba_name || info?.legal_name || info?.name || info?.brokerage}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-5 text-sm text-[#173042]/70 bg-[#EAF7FC] rounded-xl px-4 py-3">
                <CheckCircle2 size={16} className="text-[#0f6b3d]" /> Activating account for <strong className="text-[#133955]">{info?.email}</strong>
              </div>
              <form onSubmit={submit} data-testid="set-password-form" className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-[#133955] mb-2 uppercase tracking-wide">New Password</label>
                  <input data-testid="new-password" type="password" className="hpc-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#133955] mb-2 uppercase tracking-wide">Confirm Password</label>
                  <input data-testid="confirm-password" type="password" className="hpc-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
                </div>
                {error && <p data-testid="set-password-error" className="text-sm text-red-600">{error}</p>}
                <button type="submit" data-testid="set-password-submit" disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#133955] text-white px-8 py-4 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors duration-300 disabled:opacity-60">
                  {submitting && <Loader2 size={18} className="animate-spin" />}
                  {submitting ? "Activating..." : "Activate Account"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
